import {FilterCheckbox} from 'plugins/modules/filter/components/Checkbox';
import {getFilterUnit} from 'plugins/modules/filter/helpers';
import {IFilterFacet, IFilterFacetVariant} from 'plugins/modules/filter/types';
import React, {FC, ReactElement, useMemo} from 'react';
import {List, RowComponentProps, useDynamicRowHeight} from 'react-window';
import {useMountedState} from 'react-use';
import style from './style.module.scss';

const DEFAULT_ROW_HEIGHT = 32;
const LIST_OVERSCAN_COUNT = 5;
const MAX_VARIANTS_BEFORE_VIRTUALIZATION = 30;

export interface ICheckboxFilterListProps {
    filter: IFilterFacet;
    onChange: (code: string, isChecked: boolean) => void;
    variants: IFilterFacetVariant[];
    withCount?: boolean;
}

interface IRowData {
    filter: IFilterFacet;
    onChange: (code: string, isChecked: boolean) => void;
    variants: IFilterFacetVariant[];
    withCount: boolean;
}

interface IFilterCheckboxItemProps {
    variant: IFilterFacetVariant;
    filter: IFilterFacet;
    onChange: (code: string, isChecked: boolean) => void;
    withCount: boolean;
}

const FilterCheckboxItem: FC<IFilterCheckboxItemProps> = ({variant, filter, onChange, withCount}) => {
    const titleUnit = getFilterUnit(filter);
    const {code, count, isApplied, title} = variant;

    return (
        <FilterCheckbox
            code={String(code)}
            count={withCount ? count : undefined}
            isDefaultChecked={isApplied}
            onChange={onChange}
            title={`${String(title)}${titleUnit}`}
        />
    );
};

const RowComponent = ({
    index,
    style: rowStyle,
    filter,
    onChange,
    variants,
    withCount,
}: RowComponentProps<IRowData>): ReactElement => (
    <div style={{...rowStyle, padding: '0 14px 0 0'}}>
        <FilterCheckboxItem variant={variants[index]} filter={filter} onChange={onChange} withCount={withCount} />
    </div>
);

const NonVirtualizedList: FC<IRowData> = ({filter, onChange, variants, withCount}) => (
    <div className={style.main}>
        <div className={style.list}>
            {variants.map((variant) => (
                <FilterCheckboxItem
                    key={String(variant.code)}
                    variant={variant}
                    filter={filter}
                    onChange={onChange}
                    withCount={withCount}
                />
            ))}
        </div>
    </div>
);

const VirtualizedList: FC<IRowData> = ({filter, onChange, variants, withCount = true}) => {
    const isMounted = useMountedState();
    const rowHeight = useDynamicRowHeight({defaultRowHeight: DEFAULT_ROW_HEIGHT});

    if (!isMounted) {
        return null;
    }

    return (
        <div className={style.main} style={{height: rowHeight.getAverageRowHeight() * variants.length}}>
            <List
                className={style.list}
                rowComponent={RowComponent}
                rowCount={variants.length}
                rowHeight={rowHeight}
                rowProps={{filter, onChange, variants, withCount}}
                overscanCount={LIST_OVERSCAN_COUNT}
                suppressHydrationWarning={true}
            />
        </div>
    );
};

export const CheckboxFilterList: FC<ICheckboxFilterListProps> = ({filter, onChange, variants, withCount = true}) => {
    const filteredVariants = useMemo(() => variants.filter((variant) => 0 !== variant.count), [variants]);

    if (0 === filteredVariants.length) {
        return null;
    }

    const ListComponent =
        filteredVariants.length > MAX_VARIANTS_BEFORE_VIRTUALIZATION ? VirtualizedList : NonVirtualizedList;

    return <ListComponent filter={filter} onChange={onChange} variants={filteredVariants} withCount={withCount} />;
};
